import { useState } from 'react';
import { 
  ExclamationTriangleIcon, 
  CheckCircleIcon, 
  ClockIcon,
  WifiIcon,
  ServerIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { 
  AlertType, 
  AlertStatus, 
  getAlertSeverityColor, 
  getAlertStatusColor, 
  formatAlertTime,
  alertApi 
} from '../api/alertService';
import type { Alert } from '../api/alertService';

interface AlertCardProps {
  alert: Alert;
  onAcknowledge?: (alertId: string) => void;
  showActions?: boolean;
}

export default function AlertCard({ alert, onAcknowledge, showActions = true }: AlertCardProps) {
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const handleAcknowledge = async () => {
    if (!onAcknowledge) return;
    
    setIsAcknowledging(true);
    try {
      // Get current user from localStorage or context
      const currentUser = localStorage.getItem('userName') || 'System User';
      await alertApi.acknowledge(alert.id, currentUser);
      onAcknowledge(alert.id);
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    } finally {
      setIsAcknowledging(false);
    }
  };

  const getAlertIcon = () => {
    switch (alert.type) {
      case AlertType.DATA_GAP:
        return <ChartBarIcon className="h-5 w-5" />;
      case AlertType.GATEWAY_OFFLINE:
        return <WifiIcon className="h-5 w-5" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5" />;
    }
  };

  const getStatusIcon = () => {
    switch (alert.status) {
      case AlertStatus.ACTIVE:
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case AlertStatus.ACKNOWLEDGED:
        return <ClockIcon className="h-4 w-4" />;
      case AlertStatus.RESOLVED:
        return <CheckCircleIcon className="h-4 w-4" />;
      default:
        return <ExclamationTriangleIcon className="h-4 w-4" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          {/* Alert Type Icon */}
          <div className={`flex-shrink-0 p-2 rounded-lg ${getAlertSeverityColor(alert.severity)}`}>
            {getAlertIcon()}
          </div>
          
          {/* Alert Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-medium text-gray-900 truncate">
                {alert.title}
              </h3>
              
              {/* Severity Badge */}
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getAlertSeverityColor(alert.severity)}`}>
                {alert.severity.toUpperCase()}
              </span>
            </div>
            
            <p className="mt-1 text-sm text-gray-600 line-clamp-2">
              {alert.description}
            </p>
            
            {/* Metadata */}
            <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
              {alert.gatewayId && (
                <span className="flex items-center space-x-1">
                  <ServerIcon className="h-3 w-3" />
                  <span>Gateway: {alert.gatewayId}</span>
                </span>
              )}
              
              {alert.slaveId && (
                <span>Device: {alert.slaveId}</span>
              )}
              
              {alert.dataType && (
                <span>Type: {alert.dataType}</span>
              )}
              
              <span>
                {formatAlertTime(alert.firstDetectedAt)}
              </span>
            </div>
          </div>
        </div>
        
        {/* Status Badge */}
        <div className="flex-shrink-0">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getAlertStatusColor(alert.status)}`}>
            {getStatusIcon()}
            <span className="ml-1">{alert.status.toUpperCase()}</span>
          </span>
        </div>
      </div>
      
      {/* Actions */}
      {showActions && alert.status === AlertStatus.ACTIVE && (
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </button>
          
          <button
            onClick={handleAcknowledge}
            disabled={isAcknowledging}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAcknowledging ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Acknowledging...
              </>
            ) : (
              'Acknowledge'
            )}
          </button>
        </div>
      )}
      
      {/* Acknowledge/Resolve Info */}
      {(alert.status === AlertStatus.ACKNOWLEDGED || alert.status === AlertStatus.RESOLVED) && (
        <div className="mt-4 text-xs text-gray-500">
          {alert.status === AlertStatus.ACKNOWLEDGED && alert.acknowledgedBy && (
            <div>
              Acknowledged by {alert.acknowledgedBy} on {new Date(alert.acknowledgedAt!).toLocaleDateString()}
            </div>
          )}
          {alert.status === AlertStatus.RESOLVED && alert.resolvedAt && (
            <div>
              Resolved on {new Date(alert.resolvedAt).toLocaleDateString()}
            </div>
          )}
        </div>
      )}
      
      {/* Detailed Information */}
      {showDetails && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium text-gray-500">First Detected</dt>
              <dd className="text-xs text-gray-900">{new Date(alert.firstDetectedAt).toLocaleString()}</dd>
            </div>
            
            <div>
              <dt className="text-xs font-medium text-gray-500">Last Checked</dt>
              <dd className="text-xs text-gray-900">{new Date(alert.lastCheckedAt).toLocaleString()}</dd>
            </div>
            
            {alert.lastDataTimestamp && (
              <div>
                <dt className="text-xs font-medium text-gray-500">Last Data Received</dt>
                <dd className="text-xs text-gray-900">{new Date(alert.lastDataTimestamp).toLocaleString()}</dd>
              </div>
            )}
            
            {alert.metadata && Object.keys(alert.metadata).length > 0 && (
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-gray-500">Additional Info</dt>
                <dd className="text-xs text-gray-900">
                  <pre className="mt-1 whitespace-pre-wrap text-xs">
                    {JSON.stringify(alert.metadata, null, 2)}
                  </pre>
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}